<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { CursorSquareIcon, EyeIcon, HeartIcon, UsersGroupRoundedIcon } from '@solar-icons/vue/bold'
import AppMetricCell from '../../primitives/AppMetricCell/AppMetricCell.vue'
import type { MetricCellProps } from '../../primitives/AppMetricCell/app-metric-cell-types'
import type { PostResults } from '../../../types/content'

const { t } = useI18n()

const props = withDefaults(
  defineProps<{
    results?: PostResults
  }>(),
  { results: undefined },
)

const metrics = computed<MetricCellProps[]>(() => {
  const results = props.results
  if (!results || results.status !== 'measured') return []
  return [
    {
      label: t('insights.impressionsLabel'),
      value: results.impressions,
      format: 'int',
      icon: EyeIcon,
    },
    {
      label: t('insights.reachLabel'),
      value: results.reach,
      format: 'int',
      icon: UsersGroupRoundedIcon,
    },
    {
      label: t('content.metrics.engagement'),
      value: results.engagementRate * 100,
      format: 'percent',
      icon: HeartIcon,
    },
    {
      label: t('content.metrics.clicks'),
      value: results.clicks,
      format: 'int',
      icon: CursorSquareIcon,
    },
  ]
})

const emptyMessage = computed<string | null>(() => {
  if (!props.results) return t('content.results.noneYet')
  if (props.results.status === 'pending') return t('calendar.measuring')
  return null
})
</script>

<template>
  <div class="flex flex-col gap-8">
    <dl v-if="metrics.length" class="grid grid-cols-2 gap-x-8 gap-y-12">
      <div v-for="metric in metrics" :key="metric.label" class="min-w-0">
        <dt class="sr-only">{{ metric.label }}</dt>
        <dd><AppMetricCell v-bind="metric" /></dd>
      </div>
    </dl>
    <p v-else-if="emptyMessage" class="text-body-sm text-text-secondary">{{ emptyMessage }}</p>
  </div>
</template>
